// A print server as a source of jobs for the page (the other source is the USB device, see jobs.js).
// It follows one printer: polls the job list, fetches and decrypts the data of the jobs it needs,
// turns it into events (the same events a USB job is made of) and does the exact empty-job check
// in the browser: a job without ink is not shown, and a closed one is deleted from the server at
// once (server/API.md, "Empty jobs with Wi-Fi printing").
import { EventParser, InkProbe, WHY, eventWireSize } from '../core/jobs.js';

const WHY_BY_NAME = { start: WHY.START, boot: WHY.BOOT, reset: WHY.RESET, button: WHY.BUTTON, api: WHY.MANUAL };
// Closed jobs up to this many (encrypted) bytes are fetched just to see whether they have ink;
// bigger ones are assumed to have some and are only fetched when someone looks at them.
export const SMALL_JOB_BYTES = 4096;
// A device says hello at least every 2 minutes (and with every chunk): silent for longer than this = offline.
export const OFFLINE_AFTER_S = 300;

export class ServerSource {
  // client: a ReaderClient. onChange(): the list changed (new, removed, renamed, grown).
  // onEvent(job, ev): an event was added to a job whose data was already loaded (live following).
  // onError(e): a poll failed (the next one still happens).
  // onQuota(storedBytes, quotaBytes): when one of them changed. onDevice(online): the device came online / went offline (from its last hello or chunk). onActivity(): new
  // print data arrived on the server (not for the jobs that were there when the source started).
  constructor({ client, onChange = () => {}, onEvent = () => {}, onError = () => {}, onDevice = () => {}, onActivity = () => {}, onQuota = () => {}, pollMs = 1000, deleteEmpty = true }) {
    this.client = client;
    this.cb = { onChange, onEvent, onError, onDevice, onActivity, onQuota };
    this.online = null; // unknown until the first answer
    this.primed = false; // the first poll has been through
    this.pollMs = pollMs;
    this.deleteEmpty = deleteEmpty;
    this.kind = 'server';
    this.jobs = []; // shown jobs, ascending id
    this.states = new Map(); // id -> {job, parser, probe, cursor, wanted, inked, shown}
    this.after = 0; // every job up to this id is closed and synced
    this.timer = null;
    this.polling = null;
  }

  get current() {
    return this.jobs.find((j) => j.open) ?? null;
  }

  start() {
    if (this.timer) return;
    const tick = async () => {
      try {
        await this.poll();
      } catch (e) {
        this.cb.onError(e);
      }
      if (this.timer) this.timer = setTimeout(tick, this.pollMs);
    };
    this.timer = setTimeout(tick, 0);
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  // One cycle; calls overlap-safe (a second call waits for the running one).
  async poll() {
    while (this.polling) await this.polling.catch(() => {});
    this.polling = this.pollOnce();
    try {
      await this.polling;
    } finally {
      this.polling = null;
    }
  }

  // Is the device there? From the server's record of its last hello or chunk. Never fails a poll.
  async pollDevice() {
    try {
      const info = await this.client.printer();
      const seen = info.device?.last_seen_at;
      const now = (info.serverNow ?? Date.now()) / 1000;
      const online = seen != null && now - seen <= OFFLINE_AFTER_S;
      if (online !== this.online) {
        this.online = online;
        this.cb.onDevice(online);
      }
      if (info.stored_bytes !== this.stored || info.quota_bytes !== this.quota) {
        this.stored = info.stored_bytes;
        this.quota = info.quota_bytes;
        this.cb.onQuota(info.stored_bytes, info.quota_bytes);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) this.cb.onError(e);
    }
  }

  async pollOnce() {
    let changed = false;
    await this.pollDevice();
    let after = this.after;
    const seen = [];
    for (;;) {
      const page = await this.client.listJobs(after, 100);
      for (const meta of page.jobs) {
        seen.push(meta);
        if (await this.sync(meta)) changed = true;
      }
      if (!page.more || page.jobs.length === 0) break;
      after = page.jobs[page.jobs.length - 1].id;
    }
    // jobs that vanished on the server (deleted elsewhere) are dropped; the prefix of settled jobs moves on
    const ids = new Set(seen.map((m) => m.id));
    for (const [id, st] of [...this.states]) {
      if (id > this.after && !ids.has(id)) {
        this.drop(st);
        changed = true;
      }
    }
    for (const meta of seen) {
      const st = this.states.get(meta.id);
      if (meta.open || (st && !this.settled(st))) break;
      this.after = meta.id;
    }
    this.primed = true;
    if (changed) this.cb.onChange();
  }

  settled(st) {
    return !st.job.open && (st.cursor >= st.job.chunks || (!st.wanted && st.job.serverBytes > SMALL_JOB_BYTES));
  }

  // Brings one job up to date with the server's description of it. Returns whether the list changed.
  async sync(meta) {
    let st = this.states.get(meta.id);
    let changed = false;
    if (!st) {
      st = {
        job: {
          id: meta.id, remote: true, name: null, why: WHY.START, endWhy: null, interrupted: false, open: false,
          startedAt: new Date(0), lastDataAt: new Date(0), events: [], dataBytes: null, wireBytes: 0,
          chunks: 0, serverBytes: 0, loaded: false,
        },
        parser: new EventParser(), probe: new InkProbe(), cursor: 0, wanted: false, inked: false, shown: false,
      };
      this.states.set(meta.id, st);
    }
    const j = st.job;
    const prevChunks = j.chunks;
    const before = [j.name, j.endWhy, j.interrupted, j.open, j.serverBytes, j.chunks].join('|');
    if (!j.nameDirty) j.name = meta.name; // a name being typed is not overwritten by the older one from the server
    j.why = WHY_BY_NAME[meta.start_reason] ?? WHY.START;
    j.endWhy = meta.end_reason == null ? null : (WHY_BY_NAME[meta.end_reason] ?? null);
    j.interrupted = meta.cut;
    j.open = meta.open;
    j.startedAt = new Date(meta.started_at * 1000);
    j.lastDataAt = new Date(meta.last_data_at * 1000);
    j.chunks = meta.chunks;
    j.serverBytes = meta.bytes;
    const after = [j.name, j.endWhy, j.interrupted, j.open, j.serverBytes, j.chunks].join('|');
    if (before !== after) changed = true;
    if (this.primed && meta.chunks > prevChunks) this.cb.onActivity();

    const small = !meta.open && meta.bytes <= SMALL_JOB_BYTES;
    if ((meta.open || st.wanted || small) && st.cursor < meta.chunks) {
      if (await this.fetchMore(st)) changed = true;
    }
    if (this.updateVisibility(st)) changed = true;
    return changed;
  }

  // Fetches the missing chunks of a job and appends their events. Returns whether something arrived.
  async fetchMore(st) {
    const j = st.job;
    let got = false;
    for (;;) {
      const page = await this.client.stream(j.id, st.cursor);
      for (const chunk of page.chunks) {
        for (const ev of st.parser.push(chunk.bytes)) {
          j.events.push(ev);
          j.wireBytes += eventWireSize(ev);
          if (ev.t === 'data') j.dataBytes = (j.dataBytes ?? 0) + ev.bytes.length;
          if (!st.inked) st.inked = st.probe.feed(ev);
          if (st.shown) this.cb.onEvent(j, ev);
        }
        got = true;
      }
      st.cursor = page.cursor;
      j.loaded = st.cursor >= j.chunks;
      if (!page.more) break;
    }
    return got;
  }

  // Shows or hides the job according to the ink rule. Returns whether the list changed.
  updateVisibility(st) {
    const j = st.job;
    const fetchedAll = st.cursor >= j.chunks;
    // a closed job that was fetched completely and has no ink is empty; so is nothing that is still open
    const assumedInk = !j.open && !fetchedAll && j.serverBytes > SMALL_JOB_BYTES && st.cursor === 0;
    const show = st.inked || assumedInk;
    if (show && !st.shown) {
      st.shown = true;
      this.jobs.push(j);
      this.jobs.sort((a, b) => a.id - b.id);
      return true;
    }
    if (!show && !j.open && fetchedAll && j.chunks > 0) {
      this.states.delete(j.id);
      if (this.deleteEmpty) this.client.deleteJobs([j.id]).catch((e) => this.cb.onError(e));
      return false;
    }
    return false;
  }

  drop(st) {
    this.states.delete(st.job.id);
    const i = this.jobs.indexOf(st.job);
    if (i >= 0) this.jobs.splice(i, 1);
  }

  // Makes sure all the data of a job is here (when somebody wants to look at it).
  async load(job) {
    const st = this.states.get(job.id);
    if (!st) return;
    st.wanted = true;
    if (st.cursor < job.chunks) {
      await this.fetchMore(st);
      this.updateVisibility(st);
    }
  }

  async remove(jobs) {
    const ids = jobs.map((j) => j.id);
    await this.client.deleteJobs(ids);
    for (const id of ids) {
      const st = this.states.get(id);
      if (st) this.drop(st);
    }
    this.cb.onChange();
  }

  async clear() {
    await this.client.deleteJobs('all');
    this.states.clear();
    this.jobs.length = 0;
    this.after = 0;
    this.cb.onChange();
  }

  async closeCurrent() {
    await this.client.newJob();
    await this.poll();
  }

  async rename(job, name) {
    try {
      const updated = await this.client.renameJob(job.id, name);
      job.name = updated.name;
    } finally {
      job.nameDirty = false;
    }
    this.cb.onChange();
  }
}
